import { httpApi } from '@app/api/http.api';
import { ReportFilter, ReportFormat, ReportExportFile, downloadReport } from '@app/api/reportDownload';

/**
 * One generated tutorial, as the admin usage report sees it: who generated it, when,
 * from where and on what.
 *
 * `userName`, `country`, `city` and `deviceModel` are nullable on purpose — the columns
 * are new and cannot be backfilled, so rows written before they shipped carry null. The
 * UI renders those as an empty cell rather than inventing an "Unknown" placeholder.
 */
export interface UsageReportRow {
  /** Tutorial id. */
  id: number;
  createdAt: string;
  userId: number;
  userName: string | null;
  country: string | null;
  city: string | null;
  deviceModel: string | null;
  /**
   * `device` | `ip` | null — where city/country came from. An IP-derived city
   * is the carrier gateway's rather than the user's, so the table says which
   * kind of answer each row holds instead of presenting both as equal.
   */
  geoSource: string | null;
  /**
   * `pending` | `processing` | `done` | `failed`. The report counts every
   * attempt, not just the finished drawings the app shows: a failed
   * generation still spent an AI call. This column is what stops four
   * failures reading as four drawings.
   */
  status: string;
}

/** `from` / `to` are ISO timestamps; both are optional (omitted = no bound). */
export type UsageReportFilter = ReportFilter;

export interface UsageReportListRequest extends UsageReportFilter {
  page?: number;
  limit?: number;
}

export interface UsageReportListResponse {
  data: UsageReportRow[];
  /** Total matching rows, not the length of `data` — drives server-side paging. */
  total: number;
}

/** Both formats the client asked for. Shared with every other admin report. */
export type UsageReportFormat = ReportFormat;
export type UsageReportExportFile = ReportExportFile;

export const getUsageReport = (params: UsageReportListRequest = {}): Promise<UsageReportListResponse> =>
  httpApi.get<UsageReportListResponse>('ai-draw/admin/usage-report', { params }).then(({ data }) => data);

export const exportUsageReport = (
  params: UsageReportFilter = {},
  format: UsageReportFormat = 'xlsx',
): Promise<UsageReportExportFile> => downloadReport('ai-draw/admin/usage-report/export', params, format);
